import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiFillCheckCircle } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { accommodations, educationLevels, nationalities } from "../../consts";
import { signupUser } from "../../redux/Actions/userActions";
import LoadingSpinner from "../Loader/LoadingSpinner";
import MessageCard from "../MessageCard/MessageCard";
import PolicyModal from "../PolicyModal/PolicyModal";

import LanguageToggle from "../LanguageToggle/LanguageToggle";
import Meta from "../Meta/Meta";

const SignUp = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const POLICY_VERSION = "v1.0-2025-08-25";
  const MIN_SIGNUP_AGE = 16;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [policyOpen, setPolicyOpen] = useState(false);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    dateOfBirth: "",
    job: "",
    nationality: "",
    accommodation: "",
    phoneNumber: "",
    roleName: location.state?.roleName || "Student",

    consentPrivacy: false,
    confirmAge: false,
    policyVersion: POLICY_VERSION,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [strengthVisible, setStrengthVisible] = useState(false);
  const [strength, setStrength] = useState({});
  const [localErrors, setLocalErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("success");

  useEffect(() => {
    checkPasswordStrength(formData.password);
  }, [formData.password]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const isAtLeastAge = (dob, years) => {
    if (!dob) return false;
    const birth = new Date(dob);
    const now = new Date();
    const min = new Date(
      birth.getFullYear() + years,
      birth.getMonth(),
      birth.getDate()
    );
    return now >= min;
  };
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateForm = () => {
    const errors = {};
    if (!formData.firstName || formData.firstName.length < 3)
      errors.firstName = t("signup.validation.firstNameRequired");
    if (!formData.lastName || formData.lastName.length < 3)
      errors.lastName = t("signup.validation.lastNameRequired");
    if (!formData.email) errors.email = t("signup.validation.emailRequired");
    if (!emailRegex.test(formData.email.trim())) {
      errors.email = t("login.emailInvalid", {
        defaultValue: "Please enter a valid email address",
      });
    }
    if (!formData.password || formData.password.length < 6)
      errors.password = t("signup.validation.passwordTooShort");
    if (formData.password !== formData.confirmPassword)
      errors.confirmPassword = t("signup.validation.passwordMismatch");
    if (!formData.phoneNumber)
      errors.phoneNumber = t("signup.validation.phoneRequired");
    if (!formData.dateOfBirth)
      errors.dateOfBirth = t("signup.validation.dobRequired");
    if (!formData.nationality)
      errors.nationality = t("signup.validation.nationalityRequired");
    if (!formData.accommodation)
      errors.accommodation = t("signup.validation.accommodationRequired");

    // Role required
    if (!formData.roleName)
      errors.roleName = t("signup.validation.roleRequired");

    // GDPR
    if (!formData.consentPrivacy)
      errors.consentPrivacy = t("signup.validation.consentPrivacyRequired");

    if (!formData.confirmAge)
      errors.confirmAge = t("signup.validation.confirmAgeRequired");
    else if (!isAtLeastAge(formData.dateOfBirth, MIN_SIGNUP_AGE))
      errors.dateOfBirth = t("signup.validation.ageTooYoung");

    if (!formData.job)
      errors.job = t("signup.validation.educationLevelRequired");
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setLocalErrors(errors);
      return;
    }
    setLocalErrors({});
    setLoading(true);

    try {
      const payload = {
        ...formData,
        consentAt: new Date().toISOString(),
        policyVersion: POLICY_VERSION,
      };
      await dispatch(signupUser(payload));
      setMessage(t("signup.signupSuccess"));
      setMessageType("success");
      navigate("/verify");
    } catch (err) {
      // Normalize server payload to a string message
      const raw = err?.response?.data;
      const serverMsg = typeof raw === "string" ? raw : raw?.message || "";
      const text = serverMsg.toLowerCase(); // for case-insensitive includes
      const status = err?.response?.status || 0;
      const code = typeof raw === "object" ? raw?.code : "";

      if (code === "GUEST_ACCOUNT_CLAIM_REQUIRED") {
        navigate(`/signup/complete-placement?email=${encodeURIComponent(formData.email.trim().toLowerCase())}`);
        return;
      }
      if (code === "VERIFICATION_REQUIRED") {
        navigate(`/verify?email=${encodeURIComponent(formData.email.trim().toLowerCase())}`);
        return;
      }

      // Keep your includes chain
      const errorMsg = text.includes("exists")
        ? t("signup.emailExists")
        : text.includes("verification")
        ? t("signup.verificationFailed")
        : text.includes("invalid role")
        ? t("signup.validation.roleRequired")
        : text.includes("consent")
        ? t("signup.validation.consentPrivacyRequired")
        : text.includes("age confirmation")
        ? t("signup.validation.confirmAgeRequired")
        : status === 403 && text.includes("age")
        ? t("signup.validation.ageTooYoung")
        : // fallback: show server message if present, else generic
          serverMsg || t("signup.signupFailed");

      // ✅ actually show it in the MessageCard
      setMessage(errorMsg);
      setMessageType("error");

      // (optional) log details for debugging
      console.error("Signup failed", { status, server: raw });
    } finally {
      setLoading(false);
    }
  };

  const checkPasswordStrength = (password) => {
    setStrengthVisible(password.length > 0);
    setStrength({
      minLength: password.length >= 8,
      hasUpperCase: /[A-Z]/.test(password),
      hasLowerCase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSymbol: /[^A-Za-z0-9]/.test(password),
    });
  };

  const signupTitleKey =
    formData.roleName === "Teacher" ? "signup.titleTeacher" : "signup.title";
  const signupTitle = t(signupTitleKey, {
    defaultValue: t("signup.title"),
  });

  return (
    <>
      <Meta
        title="ALGermanist - Create Account"
        description="Create your ALGermanist account to enroll in German courses and track your progress."
        keywords="sign up, register, create account, ALGermanist"
      />
      <Grid
        container
        sx={{
          minHeight: "100vh",
        }}
      >
        {loading && <LoadingSpinner loading />}
        {message && (
          <MessageCard
            message={message} // this should be a global state
            type={messageType}
            onClose={() => setMessage(null)}
          />
        )}

        {/* RIGHT SIDE */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            backgroundColor: "#fff",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: 3,
          }}
        >
          <Container maxWidth="xs" sx={{ direction: isArabic ? "rtl" : "ltr" }}>
            {/* Form logo and language switcher */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1,
                mb: 2,
              }}
            >
              <Box component={Link} to="/" sx={{ display: "inline-flex" }}>
                <Box
                  component="img"
                  src="/al-germanist-logo.png"
                  alt="ALGermanist"
                  sx={{ height: { xs: 64, md: 82 }, maxWidth: 280, objectFit: "contain" }}
                />
              </Box>
              <LanguageToggle />
            </Box>
            <Typography
              variant="h6"
              fontSize="1.2rem"
              fontWeight="bold"
              mb={3}
              sx={{ mt: { xs: 0 }, textAlign: isArabic ? "right" : "left" }}
            >
              {signupTitle}
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("signup.firstName")}
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      error={!!localErrors.firstName}
                      helperText={localErrors.firstName}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        "& label": {
                          left: isArabic ? "auto" : "14px",
                          right: isArabic ? "24px" : "auto",
                          transformOrigin: isArabic ? "top right" : "top left",
                        },
                        "& legend": {
                          textAlign: isArabic ? "right" : "left",
                        },
                      }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("signup.lastName")}
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      error={!!localErrors.lastName}
                      helperText={localErrors.lastName}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        "& label": {
                          left: isArabic ? "auto" : "14px",
                          right: isArabic ? "24px" : "auto",
                          transformOrigin: isArabic ? "top right" : "top left",
                        },
                        "& legend": {
                          textAlign: isArabic ? "right" : "left",
                        },
                      }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                margin="none"
                sx={{
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "32px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              >
                <InputLabel>{t("signup.role")}</InputLabel>
                <Select
                  name="roleName"
                  value={formData.roleName}
                  label={t("signup.role")}
                  onChange={handleChange}
                >
                  <MenuItem value="Student">
                    {t("signup.studentAccount", {
                      defaultValue: t("signup.student"),
                    })}
                  </MenuItem>
                  <MenuItem value="Teacher">
                    {t("signup.teacherAccount", {
                      defaultValue: t("signup.teacher"),
                    })}
                  </MenuItem>
                </Select>
              </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("signup.email")}
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!localErrors.email}
                helperText={localErrors.email}
                margin="none"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "24px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
                </Grid>

                <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("signup.password")}
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={handleChange}
                error={!!localErrors.password}
                helperText={localErrors.password}
                margin="none"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "24px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={togglePasswordVisibility}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
                </Grid>

                <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("signup.confirmPassword")}
                name="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!localErrors.confirmPassword}
                helperText={localErrors.confirmPassword}
                margin="none"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "24px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
                </Grid>

              {strengthVisible && (
                <Grid item xs={12}>
                <Box>
                  <Typography color={strength.minLength ? "green" : "gray"}>
                    <AiFillCheckCircle />{" "}
                    {t("signup.passwordStrength.minLength")}
                  </Typography>
                  <Typography color={strength.hasUpperCase ? "green" : "gray"}>
                    <AiFillCheckCircle />{" "}
                    {t("signup.passwordStrength.uppercase")}
                  </Typography>
                  <Typography color={strength.hasLowerCase ? "green" : "gray"}>
                    <AiFillCheckCircle />{" "}
                    {t("signup.passwordStrength.lowercase")}
                  </Typography>
                  <Typography color={strength.hasNumber ? "green" : "gray"}>
                    <AiFillCheckCircle /> {t("signup.passwordStrength.number")}
                  </Typography>
                  <Typography color={strength.hasSymbol ? "green" : "gray"}>
                    <AiFillCheckCircle />{" "}
                    {t("signup.passwordStrength.symbol", {
                      defaultValue: "At least one symbol (e.g., !@#$%)",
                    })}
                  </Typography>
                </Box>
                </Grid>
              )}

                <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("signup.dateOfBirth")}
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                error={!!localErrors.dateOfBirth}
                helperText={localErrors.dateOfBirth}
                margin="none"
                InputLabelProps={{ shrink: true }}
                sx={{
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "24px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
                </Grid>

                <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                margin="none"
                sx={{
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "32px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              >
                <InputLabel>{t("signup.educationLevel")}</InputLabel>
                <Select
                  name="job"
                  value={formData.job}
                  label={t("signup.educationLevel")}
                  onChange={handleChange}
                >
                  {educationLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
              <FormControl 
                fullWidth 
                margin="none"
                sx={{
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "32px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              >
                <InputLabel>{t("signup.nationality")}</InputLabel>
                <Select
                  name="nationality"
                  value={formData.nationality}
                  label={t("signup.nationality")}
                  onChange={handleChange}
                >
                  {nationalities.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t("signup.phoneNumber")}
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                error={!!localErrors.phoneNumber}
                helperText={localErrors.phoneNumber}
                margin="none"
                sx={{
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "24px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              />
                </Grid>

                <Grid item xs={12}>
              <FormControl 
                fullWidth 
                margin="none"
                sx={{
                  "& label": {
                    left: isArabic ? "auto" : "14px",
                    right: isArabic ? "32px" : "auto",
                    transformOrigin: isArabic ? "top right" : "top left",
                  },
                  "& legend": {
                    textAlign: isArabic ? "right" : "left",
                  },
                }}
              >
                <InputLabel>{t("signup.accommodation")}</InputLabel>
                <Select
                  name="accommodation"
                  value={formData.accommodation}
                  label={t("signup.accommodation")}
                  onChange={handleChange}
                >
                  {accommodations.map((a) => (
                    <MenuItem key={a.value} value={a.value}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
                </Grid>
              </Grid>
              <Box sx={{ mt: 1, mb: 1 }}>
                <Button
                  type="button"
                  onClick={() => setPolicyOpen(true)}
                  sx={{ mb: 1 }}
                >
                  {t("signup.openPolicy")}
                </Button>

                <FormControlLabel
                  labelPlacement="end"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    mb: 1,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "1.2rem",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.6,
                    },
                  }}
                  control={
                    <Checkbox
                      name="consentPrivacy"
                      checked={formData.consentPrivacy}
                      onChange={handleChange}
                    />
                  }
                  label={`${t("signup.consentPrivacyPrefix")} ${t(
                    "signup.terms"
                  )} ${t("common.and")} ${t("signup.privacy")}`}
                />
                {localErrors.consentPrivacy && (
                  <Typography variant="caption" color="error">
                    {localErrors.consentPrivacy}
                  </Typography>
                )}

                <FormControlLabel
                  labelPlacement="end"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "1.2rem",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      lineHeight: 1.6,
                    },
                  }}
                  control={
                    <Checkbox
                      name="confirmAge"
                      checked={formData.confirmAge}
                      onChange={handleChange}
                    />
                  }
                  label={t("signup.confirmAge16")}
                />
                {localErrors.confirmAge && (
                  <Typography variant="caption" color="error">
                    {localErrors.confirmAge}
                  </Typography>
                )}
              </Box>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                color="primary"
              >
                {t("signup.submit")}
              </Button>

              <Typography sx={{ mt: 2, textAlign: "center", fontSize: 14 }}>
                {t("signup.alreadyHaveAccount")}{" "}
                <Link
                  to="/login"
                  style={{ color: "var(--primary-color)", fontWeight: "bold" }}
                >
                  {t("signup.loginNow")}
                </Link>
              </Typography>
            </form>
            <PolicyModal
              open={policyOpen}
              onClose={() => setPolicyOpen(false)}
              t={t}
              policyVersion={POLICY_VERSION}
            />
          </Container>
        </Grid>

        {/* LEFT SIDE */}
        <Grid
          item
          xs={false}
          md={6}
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "flex-start",
            alignSelf: "flex-start",
            position: "sticky",
            top: 0,
            height: "100vh",
            overflow: "hidden",
            textAlign: "left",
            px: { md: 5, lg: 7 },
            py: { md: 4, lg: 5 },
            backgroundImage:
              "linear-gradient(180deg, rgba(0,0,0,0.14) 0%, rgba(0,0,0,0.04) 42%, rgba(0,0,0,0.72) 100%), url('/signup-german-street.jpg')",
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              maxWidth: 560,
              pb: 2,
              direction: isArabic ? "rtl" : "ltr",
              textAlign: isArabic ? "right" : "left",
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                lineHeight: 1.3,
                color: "#fff",
                m: 0,
                fontSize: { md: 30, lg: 38 },
                textShadow: "0 3px 16px rgba(0,0,0,0.75)",
              }}
            >
              {t("login.sloganLine1", { ns: "ui" })}{" "}
              <Box component="span" sx={{ color: "#FFD000" }}>
                {t("login.sloganLine2", { ns: "ui" })}
              </Box>{" "}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default SignUp;
